import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import { EHairEventName, IHairTrackingEvent } from '@notino/hair-analyzer-lib';
import {
  GetProductCodeByWebIdQuery,
  GetProductCodeByWebIdQueryVariables,
} from '@notino/shared/definitions/types';

import { useTrackingContext } from '@context/tracking/TrackingContext';

import productCodeQuery from '../../../../revieve/productCode.graphql';
import {
  trackAddToCard,
  trackAddToWishList,
  trackRemoveFromWishList,
  trackViewItem,
  trackViewItemList,
} from '../../tracking';

export const useTracking = () => {
  const { getTimeFromInit } = useTrackingContext();
  const apollo = useApolloClient();

  const loadProductCodes = React.useCallback(
    async (ids: string[]) => {
      const { data } = await apollo.query<
        GetProductCodeByWebIdQuery,
        GetProductCodeByWebIdQueryVariables
      >({
        query: productCodeQuery,
        variables: { ids },
        fetchPolicy: 'cache-first',
      });
      return data?.vpProductByIds ?? [];
    },
    [apollo]
  );

  const dispatchTrackingEvent = React.useCallback(
    async (event: IHairTrackingEvent) => {
      const payload = event.payload.payload;

      switch (event.payload.name) {
        case EHairEventName.OpenProductDetails:
        case EHairEventName.OpenProductLink:
          await trackViewItem(payload, loadProductCodes);
          return;
        case EHairEventName.ViewItemList:
          await trackViewItemList([payload], loadProductCodes);
          return;
        case EHairEventName.AddToWishlist:
          await trackAddToWishList(
            payload,
            getTimeFromInit(),
            loadProductCodes
          );
          return;
        case EHairEventName.RemoveFromWishlist:
          await trackRemoveFromWishList(
            payload,
            getTimeFromInit(),
            loadProductCodes
          );
          return;
        case EHairEventName.AddToCart:
          await trackAddToCard(payload, loadProductCodes);
          return;
        default:
          break;
      }
    },
    [getTimeFromInit, loadProductCodes]
  );

  return { dispatchTrackingEvent };
};
